import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setStoredToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verify session via httpOnly cookie OR Bearer token on mount
  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        setUser(null);
        setStoredToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setStoredToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) setStoredToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Server-side logout is best-effort; we still clear local user state below
      console.warn("Logout request failed (clearing local session anyway):", error?.message || error);
    }
    setStoredToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      setStoredToken(null);
    }
  }, []);

  // Two memoized groups: identity and actions, each <= 5 deps
  const identity = useMemo(() => ({ user, loading }), [user, loading]);
  const actions = useMemo(
    () => ({ login, register, logout, refreshUser }),
    [login, register, logout, refreshUser]
  );
  const value = useMemo(() => ({ ...identity, ...actions }), [identity, actions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
